/**
 * Link between the pushadapter and pages
 * To be used to bind callback to dommanipulator messages.
 *
 * @module app/components/push/domUpdates/DomPushAdaptor
 */
/**
 * Elements inside this guy will be known as stackItem
 *
 * @type {Object}
 */
var subscriptionStack = {};

/**
 * Map of message node type to mb topics
 * @type {{events: string, markets: string, selections: string}}
 * @private
 */
var topics = {
    events: 'push.eventUpdate',
    markets: 'push.marketUpdate',
    selections: 'push.selectionUpdate',
};

/**
 * This function will call the callback contained in a stackItem
 *
 * @param  {Object} stackItem [description]
 * @param  {Object} message Object containing  id, attribute, value
 *
 * @returns {void}
 *
 * @private
 */
const triggerCallback = function (stackItem, message) {

    if (typeof stackItem.callback === 'function') {
        stackItem.callback(message);
    }
};

/**
 * Checks whether an entity is associated with an event we should subscribe to
 * @param   {Object}    entity  The PDS entity we're considering a subscription to
 * @returns {boolean}           Whether entity has an id and does not have the dontSubscribe flag
 * @private
 */
var isValidPushCandidate = function (entity) {

    return (!entity.dontSubscribe);
};

/**
 * Publishes an inbound pds delta message on the messagebus
 * @param {Object}  message Inbound PDS delta message
 * @private
 */
var publishDelta = function (message) {

    if (window.WH && window.WH.messageBus) {
        window.WH.messageBus.publish(topics[message.nodeType], message.entity);
    }
};

/**
 * Public functions.
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * Generates a subscription to DOMManipulator changes
     *
     * @param {Object} entity containing entity id
     * @param {Function} callback process any notification affecting this entity
     * @param {Boolean} preventPropagation flag that if true, prevents propagation in selection->mkt->evt hierarchy
     *
     * @returns {Boolean}
     *
     * @public
     */
    subscribe: function (entity, callback, preventPropagation) {

        if (typeof callback === 'function' && isValidPushCandidate(entity)) {

            subscriptionStack[entity.id] = {
                id: entity.id,
                callback: callback,
                preventPropagation: preventPropagation,
            };

            return true;
        }

        return false;
    },

    /**
     * Remove single entity
     *
     * @param   {String} entityId
     *
     * @returns {void}
     *
     * @public
     */
    unsubscribe: function (entityId) {

        delete subscriptionStack[entityId];
    },

    /**
     * Clears the subscriptions stack
     *
     * @returns {void}
     *
     * @public
     */
    unsubscribeAll: function () {

        subscriptionStack = {};
    },

    /**
     * This function receive messages from DOMManipulator to trigger callbacks
     *
     * @param   {Object}    message     object containing  id, attribute, value
     *
     * @returns {void}
     *
     * @public
     */
    push: function (message) {

        var stackItem;

        if (subscriptionStack.hasOwnProperty(message.id)) {
            stackItem = subscriptionStack[message.id];
            triggerCallback(stackItem, message);
        }
    },

    /**
     * Gets the callback from the subscription stack based on the initial topic subscribed to and calls it.
     * Only valid if batch subscribe.
     *
     * @param   {Object}    message     object comming from diffusion
     *
     * @returns {void}
     *
     * @public
     */
    notifyNewNode: function (message) {

        publishDelta(message);

        var stackItem = subscriptionStack[message.topics.parent];

        if (stackItem && typeof stackItem.callback === 'function') {
            triggerCallback(stackItem, message);
        }
    },

    /**
     * Indicates if for the given entity updates won't propagate, allowing page specific propagation logic
     *
     * @param {String} entityId An id of a model entity
     *
     * @returns {Boolean} true only if the propagation in selection->mkt->evt hierarchy is not allowed
     *
     * @public
     */
    preventPropagation: function preventPropagation(entityId) {

        var stackItem = subscriptionStack[entityId];
        return stackItem && stackItem.preventPropagation;
    },

    /**
     * Expose publishDelta for us to allow for publishing to message bus
     * @param {string} nodeType events, markets or selections
     * @param {Object} entity the entity to publish
     */
    publish: function publish (nodeType, entity) {
        publishDelta({
            nodeType: nodeType,
            entity: entity,
        });
    },
};

export default api;
